/**
 * Determines the vehicle a driver used the most across a work period's
 * activities, so the extend-day flow can prefill the vehicle (and, through it,
 * the facility/department) instead of forcing a manual pick.
 *
 * Activities reference vehicles by VIN / registration number rather than by id,
 * so the result is expressed the same way and matched against the vehicle list
 * by the caller. "Most used" is measured by total time spent in the vehicle,
 * falling back to activity count and finally to first appearance for ties.
 */

export type ActivityVehicleInput = {
  vehicleVin?: string | null
  vehicleRegistrationNumber?: string | null
  datetimeStart?: string | null
  datetimeEnd?: string | null
}

export type MostUsedVehicle = {
  vehicleVin: string | null
  vehicleRegistrationNumber: string | null
}

const normalize = (value?: string | null) => value?.trim() || ''

const getDurationMs = (activity: ActivityVehicleInput) => {
  if (!activity.datetimeStart || !activity.datetimeEnd) return 0

  const start = new Date(activity.datetimeStart).getTime()
  const end = new Date(activity.datetimeEnd).getTime()

  if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) {
    return 0
  }

  return end - start
}

export const getMostUsedVehicleFromActivities = (
  activities: ReadonlyArray<ActivityVehicleInput> | null | undefined
): MostUsedVehicle | null => {
  if (!activities?.length) return null

  type Group = MostUsedVehicle & {
    duration: number
    count: number
    order: number
  }

  const groups = new Map<string, Group>()

  activities.forEach((activity, index) => {
    const vin = normalize(activity.vehicleVin)
    const registrationNumber = normalize(activity.vehicleRegistrationNumber)
    // VIN is the stable identifier; fall back to the registration number.
    const key = vin || registrationNumber
    if (!key) return

    const duration = getDurationMs(activity)
    const existing = groups.get(key)

    if (existing) {
      existing.duration += duration
      existing.count += 1
    } else {
      groups.set(key, {
        vehicleVin: vin || null,
        vehicleRegistrationNumber: registrationNumber || null,
        duration,
        count: 1,
        order: index,
      })
    }
  })

  let best: Group | null = null

  for (const group of groups.values()) {
    if (
      !best ||
      group.duration > best.duration ||
      (group.duration === best.duration && group.count > best.count) ||
      (group.duration === best.duration &&
        group.count === best.count &&
        group.order < best.order)
    ) {
      best = group
    }
  }

  if (!best) return null

  return {
    vehicleVin: best.vehicleVin,
    vehicleRegistrationNumber: best.vehicleRegistrationNumber,
  }
}
